import React, { FC } from 'react';
import { MessageDialog } from 'src/components/widgets/dialog';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { SwitchWizardStep, SwitchWizardStepIndicator } from './switchWizardSteps';

export interface CustomPlanFinancialPreview {
    isEstimate: boolean;
    chargedByThisEndpoint: number;
    currentPlan: {
        monthlyPrice: number;
        currencyCode: string;
    };
    newPlan: {
        monthlyPrice: number;
        currencyCode: string;
    };
    proration: {
        available: boolean;
        estimatedUnusedAmount: number;
        currencyCode: string;
        estimatedUnusedAmountInNewCurrency: number;
        newCurrencyCode: string;
        requiresManualSettlement: boolean;
    };
    paymentOnPlanChange: {
        mode: string;
        estimatedCreditApplied: number;
        estimatedRefundValue: number;
        estimatedNetAmount: number;
        currencyCode: string;
        nextRenewalAtUtc: string | null;
        nextRenewalAmount: number;
    };
    warnings: readonly string[];
}

interface CustomPlanFinancialPreviewDialogProps {
    preview: CustomPlanFinancialPreview;
    onContinue: () => void;
}

const Section = styled.section`
    margin-top: 16px;
`;

const Summary = styled.dl`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px 16px;
    margin: 8px 0 0;

    dt,
    dd {
        margin: 0;
    }

    dd {
        font-weight: 600;
        text-align: right;
    }
`;

const WarningList = styled.ul`
    margin: 8px 0 0;
    padding-left: 20px;
`;

const formatAmount = (amount: number, currencyCode: string): string => `${currencyCode} ${amount.toFixed(2)}`;

const CustomPlanFinancialPreviewDialog: FC<CustomPlanFinancialPreviewDialogProps> = ({ preview, onContinue }) => {
    const { currentPlan, newPlan, paymentOnPlanChange, proration } = preview;

    return (
        <MessageDialog
            showCloseButton={false}
            titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.Subscription} />}
            message={
                <>
                    <h3>{T._('Financial preview', 'Admin')}</h3>
                    <p>{T._('Review the estimated financial impact of the plan change before continuing.', 'Admin')}</p>
                    {preview.isEstimate && <p>{T._('Amounts are estimates and may vary when the subscription is processed.', 'Admin')}</p>}

                    <Section>
                        <strong>{T._('Plan pricing', 'Admin')}</strong>
                        <Summary>
                            <dt>{T._('Current monthly price', 'Admin')}</dt>
                            <dd>{formatAmount(currentPlan.monthlyPrice, currentPlan.currencyCode)}</dd>
                            <dt>{T._('New monthly price', 'Admin')}</dt>
                            <dd>{formatAmount(newPlan.monthlyPrice, newPlan.currencyCode)}</dd>
                        </Summary>
                    </Section>

                    <Section>
                        <strong>{T._('Plan change', 'Admin')}</strong>
                        <Summary>
                            <dt>{T._('Estimated net amount', 'Admin')}</dt>
                            <dd>{formatAmount(paymentOnPlanChange.estimatedNetAmount, paymentOnPlanChange.currencyCode)}</dd>
                            <dt>{T._('Payment mode', 'Admin')}</dt>
                            <dd>{paymentOnPlanChange.mode}</dd>
                            <dt>{T._('Charged by this operation', 'Admin')}</dt>
                            <dd>{formatAmount(preview.chargedByThisEndpoint, paymentOnPlanChange.currencyCode)}</dd>
                            <dt>{T._('Credit applied', 'Admin')}</dt>
                            <dd>{formatAmount(paymentOnPlanChange.estimatedCreditApplied, paymentOnPlanChange.currencyCode)}</dd>
                            <dt>{T._('Estimated refund', 'Admin')}</dt>
                            <dd>{formatAmount(paymentOnPlanChange.estimatedRefundValue, paymentOnPlanChange.currencyCode)}</dd>
                            <dt>{T._('Next renewal amount', 'Admin')}</dt>
                            <dd>{formatAmount(paymentOnPlanChange.nextRenewalAmount, paymentOnPlanChange.currencyCode)}</dd>
                            {paymentOnPlanChange.nextRenewalAtUtc && (
                                <>
                                    <dt>{T._('Next renewal date', 'Admin')}</dt>
                                    <dd>{new Date(paymentOnPlanChange.nextRenewalAtUtc).toLocaleDateString()}</dd>
                                </>
                            )}
                        </Summary>
                    </Section>

                    {proration.available && (
                        <Section>
                            <strong>{T._('Proration', 'Admin')}</strong>
                            <Summary>
                                <dt>{T._('Estimated unused amount', 'Admin')}</dt>
                                <dd>{formatAmount(proration.estimatedUnusedAmount, proration.currencyCode)}</dd>
                                <dt>{T._('Estimated unused amount in the new currency', 'Admin')}</dt>
                                <dd>{formatAmount(proration.estimatedUnusedAmountInNewCurrency, proration.newCurrencyCode)}</dd>
                                {proration.requiresManualSettlement && (
                                    <>
                                        <dt>{T._('Manual settlement', 'Admin')}</dt>
                                        <dd>{T._('Required', 'Admin')}</dd>
                                    </>
                                )}
                            </Summary>
                        </Section>
                    )}

                    {preview.warnings.length > 0 && (
                        <Section>
                            <strong>{T._('Warnings', 'Admin')}</strong>
                            <WarningList>
                                {preview.warnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </WarningList>
                        </Section>
                    )}
                </>
            }
            buttons={[
                {
                    label: T._('Continue', 'Admin'),
                    onClick: onContinue
                }
            ]}
        />
    );
};

export default CustomPlanFinancialPreviewDialog;
